import { useCallback, useRef, useState } from 'react';

type TUpdater<T> = T | ((prev: T) => T);
type TSetDraft<T> = (updater: TUpdater<T>) => void;

interface IUseDraftStateReturn<T> {
  draft: T,
  getDraft: () => T,
  setDraft: TSetDraft<T>,
  resetTo: (value: T) => void,
  resetKey: number,
}

const useDraftState = <T>(initial: T): IUseDraftStateReturn<T> => {
  const draftRef = useRef<T>(initial);
  const [resetKey, setResetKey] = useState(0);

  const getDraft = useCallback((): T => draftRef.current, []);

  const setDraft = useCallback<TSetDraft<T>>((updater) => {
    draftRef.current = typeof updater === 'function'
      ? (updater as (prev: T) => T)(draftRef.current)
      : updater;
  }, []);

  const resetTo = useCallback((value: T): void => {
    draftRef.current = value;
    setResetKey((k) => k + 1);
  }, []);

  return { draft: draftRef.current, getDraft, setDraft, resetTo, resetKey };
};

export default useDraftState;
export type { TUpdater, TSetDraft };
